import { getLocationBySlug, isLocationQualified } from "./locationRegistry";
import { getSeoEntryByRoute } from "./seoRegistry";
import { getGuideBySlug } from "./guideRegistry";
import { getIndustryBySlug } from "./industryRegistry";
import { getFeatureBySlug } from "./featureRegistry";
import { getReportBySlug } from "./reportRegistry";
import { getMethodologyArticleBySlug } from "./methodologyRegistry";
import { getCompareArticleBySlug } from "./compareRegistry";

export interface BreadcrumbNode {
  label: string;
  path: string;
}

const PRIMARY_DOMAIN = "https://getbiznearyou.com";

// Routes where breadcrumbs must NEVER be rendered or emitted as schema
const NO_BREADCRUMB_PREFIXES = [
  "/login",
  "/signup",
  "/checkout",
  "/dashboard",
  "/settings",
  "/account",
  "/saved",
  "/history",
  "/app",
  "/admin",
  "/auth/callback"
];

export function shouldShowBreadcrumbs(path: string): boolean {
  if (!path) return false;
  const cleanPath = path.split("?")[0].split("#")[0];
  if (cleanPath === "/" || cleanPath === "") return false;

  for (const prefix of NO_BREADCRUMB_PREFIXES) {
    if (cleanPath === prefix || cleanPath.startsWith(`${prefix}/`)) {
      return false;
    }
  }

  return true;
}

export function createAbsoluteUrl(path: string, siteUrl: string = PRIMARY_DOMAIN): string {
  const cleanPath = path.split("?")[0].split("#")[0];
  if (/^https?:\/\//i.test(cleanPath)) {
    return cleanPath;
  }

  const normalizedSiteUrl = siteUrl.replace(/\/+$/, "");
  const normalizedPath = cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;

  if (normalizedPath === "/") return `${normalizedSiteUrl}/`;
  return `${normalizedSiteUrl}${normalizedPath.replace(/\/+$/, "")}`;
}

/**
 * Derives the complete, human-readable breadcrumb hierarchy array for any given route path.
 * Always includes { label: "Home", path: "/" } as the first element.
 */
export function getBreadcrumbNodes(path: string): BreadcrumbNode[] {
  const cleanPath = path.split("?")[0].split("#")[0];
  const homeNode: BreadcrumbNode = { label: "Home", path: "/" };

  if (cleanPath === "/" || cleanPath === "" || !shouldShowBreadcrumbs(cleanPath)) {
    return [homeNode];
  }

  // 1. Static Core Route Mapping
  const staticRouteMap: Record<string, BreadcrumbNode[]> = {
    "/about": [homeNode, { label: "About", path: "/about" }],
    "/contact": [homeNode, { label: "Contact Us", path: "/contact" }],
    "/pricing": [homeNode, { label: "Pricing & Plans", path: "/pricing" }],
    "/how-it-works": [homeNode, { label: "How It Works", path: "/how-it-works" }],
    "/faq": [homeNode, { label: "FAQ", path: "/faq" }],
    "/privacy": [homeNode, { label: "Privacy Policy", path: "/privacy" }],
    "/terms": [homeNode, { label: "Terms of Service", path: "/terms" }],
    "/why-biznearyou": [homeNode, { label: "Why BizNearYou", path: "/why-biznearyou" }],
    "/how-data-is-collected": [homeNode, { label: "How Data Is Collected", path: "/how-data-is-collected" }],
    "/how-scores-are-calculated": [homeNode, { label: "Score Calculation", path: "/how-scores-are-calculated" }],
    "/how-hiring-signals-work": [homeNode, { label: "Hiring Signals Method", path: "/how-hiring-signals-work" }],
    "/why-businesses-before-job-listings": [homeNode, { label: "Unposted Opportunities", path: "/why-businesses-before-job-listings" }],
    "/docs": [homeNode, { label: "Documentation", path: "/docs" }],
    "/find-jobs": [homeNode, { label: "Find Jobs", path: "/find-jobs" }],
    "/hidden-jobs": [homeNode, { label: "Hidden Jobs", path: "/hidden-jobs" }],
    "/local-internships": [homeNode, { label: "Local Internships", path: "/local-internships" }],
    "/first-jobs": [homeNode, { label: "First Jobs", path: "/first-jobs" }],
    "/businesses-hiring-near-me": [homeNode, { label: "Businesses Hiring Near Me", path: "/businesses-hiring-near-me" }],
    "/explore": [homeNode, { label: "Explore Directory", path: "/explore" }],
    "/for": [homeNode, { label: "Audience Directory", path: "/for" }],
    "/locations": [homeNode, { label: "Locations", path: "/jobs/locations" }],
    "/jobs/locations": [homeNode, { label: "Job Search Locations", path: "/jobs/locations" }],
    "/businesses-hiring/locations": [homeNode, { label: "Hiring Signal Locations", path: "/businesses-hiring/locations" }],
    "/internships/locations": [homeNode, { label: "Internship Locations", path: "/internships/locations" }],
    "/industries": [homeNode, { label: "Industries", path: "/industries" }],
    "/reports": [homeNode, { label: "Reports", path: "/reports" }],
    "/compare": [homeNode, { label: "Comparisons", path: "/compare" }],
    "/guides": [homeNode, { label: "Guides", path: "/guides" }],
    "/features": [homeNode, { label: "Features", path: "/features" }],
    // Audience Landing Pages
    "/for-job-seekers": [homeNode, { label: "Audience Directory", path: "/for" }, { label: "For Job Seekers", path: "/for-job-seekers" }],
    "/for-recruiters": [homeNode, { label: "Audience Directory", path: "/for" }, { label: "For Recruiters", path: "/for-recruiters" }],
    "/for-staffing-agencies": [homeNode, { label: "Audience Directory", path: "/for" }, { label: "For Staffing Agencies", path: "/for-staffing-agencies" }],
    "/for-career-coaches": [homeNode, { label: "Audience Directory", path: "/for" }, { label: "For Career Coaches", path: "/for-career-coaches" }],
    "/for-workforce-organizations": [homeNode, { label: "Audience Directory", path: "/for" }, { label: "For Workforce Organizations", path: "/for-workforce-organizations" }],
    "/for-college-career-centers": [homeNode, { label: "Audience Directory", path: "/for" }, { label: "For College Career Centers", path: "/for-college-career-centers" }],
    "/for-sales-teams": [homeNode, { label: "Audience Directory", path: "/for" }, { label: "For Sales Teams", path: "/for-sales-teams" }],
    "/for-business-development": [homeNode, { label: "Audience Directory", path: "/for" }, { label: "For Business Development", path: "/for-business-development" }],
    "/for-agencies": [homeNode, { label: "Audience Directory", path: "/for" }, { label: "For Agencies", path: "/for-agencies" }],
    // Methodology Pages
    "/hiring-signal-methodology": [homeNode, { label: "Methodology", path: "/hiring-signal-methodology" }, { label: "Hiring Signal Methodology", path: "/hiring-signal-methodology" }],
    "/opportunity-score-methodology": [homeNode, { label: "Methodology", path: "/hiring-signal-methodology" }, { label: "Opportunity Score Methodology", path: "/opportunity-score-methodology" }],
    "/data-sources": [homeNode, { label: "Data & Transparency", path: "/data-sources" }, { label: "Data Sources", path: "/data-sources" }],
    "/accuracy-and-limitations": [homeNode, { label: "Data & Transparency", path: "/data-sources" }, { label: "Accuracy & Limitations", path: "/accuracy-and-limitations" }],
    // Prospecting Pages
    "/business-hiring-signals": [homeNode, { label: "Business Prospecting", path: "/local-business-prospecting" }, { label: "Business Hiring Signals", path: "/business-hiring-signals" }],
    "/local-business-prospecting": [homeNode, { label: "Business Prospecting", path: "/local-business-prospecting" }, { label: "Local Business Prospecting", path: "/local-business-prospecting" }],
    "/employer-prospecting": [homeNode, { label: "Business Prospecting", path: "/local-business-prospecting" }, { label: "Employer Prospecting", path: "/employer-prospecting" }],
    "/staffing-prospecting": [homeNode, { label: "Business Prospecting", path: "/local-business-prospecting" }, { label: "Staffing Prospecting", path: "/staffing-prospecting" }],
    "/recruiter-business-search": [homeNode, { label: "Business Prospecting", path: "/local-business-prospecting" }, { label: "Recruiter Business Search", path: "/recruiter-business-search" }]
  };

  if (staticRouteMap[cleanPath]) {
    return staticRouteMap[cleanPath];
  }

  // 2. Dynamic Feature Sub-pages: /features/:slug
  if (cleanPath.startsWith("/features/")) {
    const slug = cleanPath.replace("/features/", "");
    const feature = getFeatureBySlug(slug);
    const label = feature ? feature.name : slugToHumanLabel(slug);
    return [
      homeNode,
      { label: "Features", path: "/features" },
      { label, path: cleanPath }
    ];
  }

  // 3. Dynamic Guide Sub-pages: /guides/:slug or /guide/:slug
  if (cleanPath.startsWith("/guides/") || cleanPath.startsWith("/guide/")) {
    const slug = cleanPath.replace(/^\/(guides|guide)\//, "");
    const guide = getGuideBySlug(slug);
    const label = guide ? guide.title : slugToHumanLabel(slug);
    return [
      homeNode,
      { label: "Guides", path: "/guides" },
      { label, path: cleanPath }
    ];
  }

  // 4. Dynamic Industry Sub-pages: /industry/:slug
  if (cleanPath.startsWith("/industry/")) {
    const slug = cleanPath.replace("/industry/", "");
    const ind = getIndustryBySlug(slug);
    const label = ind ? ind.name : slugToHumanLabel(slug);
    return [
      homeNode,
      { label: "Industries", path: "/industries" },
      { label, path: cleanPath }
    ];
  }

  // 5. Dynamic Report Sub-pages: /report/:slug or /reports/:slug
  if (cleanPath.startsWith("/report/") || cleanPath.startsWith("/reports/")) {
    const slug = cleanPath.replace(/^\/(reports|report)\//, "");
    const report = getReportBySlug(slug);
    const label = report ? report.title : slugToHumanLabel(slug);
    return [
      homeNode,
      { label: "Reports", path: "/reports" },
      { label, path: cleanPath }
    ];
  }

  // 6. Dynamic Compare Sub-pages: /compare/:slug
  if (cleanPath.startsWith("/compare/")) {
    const slug = cleanPath.replace("/compare/", "");
    const compare = getCompareArticleBySlug(slug);
    const label = compare ? compare.title : slugToHumanLabel(slug);
    return [
      homeNode,
      { label: "Comparisons", path: "/compare" },
      { label, path: cleanPath }
    ];
  }

  // 7. Dynamic Methodology Sub-pages: /methodology/:slug
  if (cleanPath.startsWith("/methodology/")) {
    const slug = cleanPath.replace("/methodology/", "");
    const article = getMethodologyArticleBySlug(slug);
    const label = article ? article.h1 : slugToHumanLabel(slug);
    return [
      homeNode,
      { label: "Methodology", path: "/hiring-signal-methodology" },
      { label, path: cleanPath }
    ];
  }

  // 8. Dynamic Location Sub-pages: /jobs/:slug, /businesses-hiring/:slug, /internships/:slug
  const locationPrefixes = [
    { prefix: "/businesses-hiring/", parentLabel: "Businesses Hiring", parentPath: "/businesses-hiring/locations" },
    { prefix: "/internships/", parentLabel: "Internships", parentPath: "/internships/locations" },
    { prefix: "/jobs/", parentLabel: "Jobs", parentPath: "/jobs/locations" }
  ];

  for (const { prefix, parentLabel, parentPath } of locationPrefixes) {
    if (cleanPath.startsWith(prefix) && cleanPath !== parentPath) {
      const rawSlug = cleanPath.replace(prefix, "");
      const loc = getLocationBySlug(rawSlug);

      if (loc) {
        const cityName = loc.cityName;
        const stateName = loc.stateName;
        const countryName = loc.countryName;
        const countryCode = loc.countryCode;

        if (countryCode && countryCode.toUpperCase() !== "US") {
          return [
            homeNode,
            { label: parentLabel, path: parentPath },
            { label: countryName, path: `/locations?country=${countryCode}` },
            { label: cityName, path: cleanPath }
          ];
        }

        return [
          homeNode,
          { label: parentLabel, path: parentPath },
          { label: stateName || "State", path: parentPath },
          { label: cityName, path: cleanPath }
        ];
      }

      // Fallback if slug not found in registry
      return [
        homeNode,
        { label: parentLabel, path: parentPath },
        { label: slugToHumanLabel(rawSlug), path: cleanPath }
      ];
    }
  }

  // 9. State Hub Pages: /state/:slug
  if (cleanPath.startsWith("/state/")) {
    const slug = cleanPath.replace("/state/", "");
    return [
      homeNode,
      { label: "Locations", path: "/jobs/locations" },
      { label: slugToHumanLabel(slug), path: cleanPath }
    ];
  }

  // 10. Check Centralized SEO Registry for fallback label
  const regEntry = getSeoEntryByRoute(cleanPath);
  if (regEntry) {
    return [
      homeNode,
      { label: regEntry.breadcrumbLabel || regEntry.h1 || slugToHumanLabel(cleanPath.replace(/^\//, "")), path: cleanPath }
    ];
  }

  // Default fallback for any unspecified public route
  const lastSegment = cleanPath.split("/").pop() || "";
  return [
    homeNode,
    { label: slugToHumanLabel(lastSegment), path: cleanPath }
  ];
}

/**
 * Generates valid Schema.org BreadcrumbList JSON-LD object for any path.
 * Returns null if breadcrumbs should not be shown.
 */
export function generateBreadcrumbJsonLd(path: string, siteUrl: string = PRIMARY_DOMAIN): object | null {
  if (!shouldShowBreadcrumbs(path)) {
    return null;
  }

  const nodes = getBreadcrumbNodes(path);
  if (!nodes || nodes.length < 2) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": nodes.map((node, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": node.label,
      "item": createAbsoluteUrl(node.path, siteUrl)
    }))
  };
}

/**
 * Converts raw URL slugs (e.g., "opportunity-score", "tokyo-13-jp") into clean, human-readable labels.
 */
export function slugToHumanLabel(slug: string): string {
  if (!slug) return "";
  
  // Custom manual overrides for known slugs
  const overrides: Record<string, string> = {
    "docs": "Documentation",
    "faq": "FAQ",
    "crm": "CRM",
    "seo": "SEO",
    "b2b": "B2B",
    "us": "United States",
    "jp": "Japan",
    "uk": "United Kingdom",
    "ca": "Canada",
    "au": "Australia"
  };

  const lower = slug.toLowerCase();
  if (overrides[lower]) return overrides[lower];

  // Clean trailing country/id suffixes like "-13-jp" or "-il"
  let cleaned = slug
    .replace(/-\d+-[a-z]{2}$/i, "") // Removes "-13-jp"
    .replace(/-/g, " ");

  return cleaned
    .split(" ")
    .map(word => {
      const wLower = word.toLowerCase();
      if (overrides[wLower]) return overrides[wLower];
      if (wLower === "and" || wLower === "for" || wLower === "or" || wLower === "of" || wLower === "in" || wLower === "to" || wLower === "with") {
        return wLower;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ")
    .replace(/^([a-z])/, match => match.toUpperCase());
}
